import { Injectable } from '@angular/core';
import {
    Area,
    AreaControllerService,
    Company,
    CompanyControllerFind200Response,
    CompanyControllerService,
    DepartmentControllerFind200Response,
    DepartmentControllerService,
    JobPosition,
    JobPositionControllerFind200Response,
    JobPositionControllerService,
    User,
    UserControllerFind200Response,
    UserControllerService,
    UsersFilter,
    FileUploadControllerService,
    JobCategoryControllerService,
    JobCategoryControllerFind200Response,
    JobCategory,
    UserControllerJsonUserPublications200Response,
    InactiveReasonControllerService,
    PersonalRequisitionWithRelations,
    UserHiringOptionsControllerService,
    VacancyWithRelations,
    DirectoryDepartment,
    DirectoryPage,
    UserDirectoryControllerService,
} from 'projects/api-swagger-client/src';
import {
    catchError,
    forkJoin,
    map,
    Observable,
    of,
    tap,
    throwError,
} from 'rxjs';
import { Department } from '../../../projects/api-swagger-client/src/model/department';
import { HumanCapitalByCompanyControllerService } from '../../../projects/api-swagger-client/src/api/humanCapitalByCompanyController.service';
import { CityControllerService } from '../../../projects/api-swagger-client/src/api/cityController.service';

@Injectable({
    providedIn: 'root',
})
export class UserService {
    constructor(
        private userControllerService: UserControllerService,
        private companyControllerService: CompanyControllerService,
        private departmentControllerService: DepartmentControllerService,
        private areaController: AreaControllerService,
        private jobPositionController: JobPositionControllerService,
        private fileUploadControllerService: FileUploadControllerService,
        private jobCategoryControllerService: JobCategoryControllerService,
        private humanCapitalByCompanyControllerService: HumanCapitalByCompanyControllerService,
        private inactiveReasonControllerService: InactiveReasonControllerService,
        private cityControllerService: CityControllerService,
        private hiringOptionsControllerService: UserHiringOptionsControllerService,
        private userDirectoryControllerService: UserDirectoryControllerService
    ) { }

    /**
     * Requisiciones que se pueden ligar a un empleado. El backend ya excluye canceladas
     * y rechazadas; `where` sirve para acotar por empresa o traer una en específico.
     */
    getHiringRequisitions = (where: object = {}): Observable<PersonalRequisitionWithRelations[]> => {
        const filter: any = JSON.stringify({ where, order: ['createdOn DESC'] });
        return this.hiringOptionsControllerService
            .userHiringOptionsControllerFindRequisitions(filter)
            .pipe(map((response) => response.data ?? []));
    };

    /** Vacantes que se pueden ligar a un empleado (sin canceladas). */
    getHiringVacancies = (where: object = {}): Observable<VacancyWithRelations[]> => {
        const filter: any = JSON.stringify({ where, order: ['createdOn DESC'] });
        return this.hiringOptionsControllerService
            .userHiringOptionsControllerFindVacancies(filter)
            .pipe(map((response) => response.data ?? []));
    };
    /**
     * @param forRequisition Alcance del formulario de requisiciones: suma los
     * usuarios de las empresas hermanas.
     */
    getInactivateReasons = (): Observable<any> => {
        return this.inactiveReasonControllerService.inactiveReasonControllerFind(
            {}
        );
    };

    mapUserData = (user: User) => ({
        ...user,
        /*    createdOn: moment(user.createdOn).fromNow(),
           modifiedOn: moment(user.modifiedOn).fromNow(), */
    });

    countUsers = () => this.userControllerService.userControllerCount();

    /** Página del directorio de contactos; el backend busca, filtra por empresa y ordena. */
    searchDirectory = (
        search: string,
        departmentId: string | null,
        page: number,
        limit: number
    ): Observable<DirectoryPage> => {
        return this.userDirectoryControllerService
            .userDirectoryControllerSearch(search || undefined, departmentId || undefined, page, limit)
            .pipe(map((response) => response.data as DirectoryPage));
    };

    getDirectoryDepartments = (): Observable<DirectoryDepartment[]> => {
        return this.userDirectoryControllerService
            .userDirectoryControllerDepartments()
            .pipe(map((response) => response.data ?? []));
    };

    getCompanies = ({
        active,
        direction,
        pageIndex,
    }: any): Observable<Company[]> => {
        return this.companyControllerService.companyControllerFind({}).pipe(
            tap((response: CompanyControllerFind200Response) => {
                if (!response.success || response.statusCode !== 200) {
                    throw new Error('Error en la respuesta del servidor');
                }
            }),
            catchError((error) => {
                console.error('Error fetching companies:', error);
                return [];
            })
        );
    };

    getDepartment = (
        { active, direction, pageIndex }: any,
        companyId?: string
    ): Observable<Department[]> => {
        const where: any = {};
        if (companyId) where.companyId = companyId;
        const filters: any | Department = JSON.stringify({
            where: where,
            order: ['name ASC']
        });
        return this.departmentControllerService
            .departmentControllerFind(filters)
            .pipe(
                tap((response: DepartmentControllerFind200Response) => {
                    if (!response.success || response.statusCode !== 200) {
                        throw new Error('Error en la respuesta del servidor');
                    }
                }),
                catchError((error) => {
                    console.error('Error fetching companies:', error);
                    return [];
                })
            );
    };

    getAreas = ({ active, direction, pageIndex }: any): Observable<Area[]> => {
        return this.areaController.areaControllerFind({}).pipe(
            tap((response: JobPositionControllerFind200Response) => {
                if (!response.success || response.statusCode !== 200) {
                    throw new Error('Error en la respuesta del servidor');
                }
            }),
            catchError((error) => {
                console.error('Error fetching companies:', error);
                return [];
            })
        );
    };

    getJobPositions = ({
        active,
        direction,
        pageIndex,
    }: any, companyId?: string): Observable<JobPosition[]> => {
        const filter: any = JSON.stringify({
            order: ['name ASC'],
            ...(companyId ? { where: { companyId } } : {}),
            include: [{
                relation: 'department',
                scope: { fields: ['id', 'name'] },

            }, { relation: 'businessUnits', scope: { fields: ['id', 'name'] } }],
        });
        return this.jobPositionController.jobPositionControllerFind(filter).pipe(
            tap((response: JobPositionControllerFind200Response) => {
                if (!response.success || response.statusCode !== 200) {
                    throw new Error('Error en la respuesta del servidor');
                }
            }),
            catchError((error) => {
                console.error('Error fetching companies:', error);
                return [];
            })
        );
    };

    getJobCategories = ({
        active,
        direction,
        pageIndex,
    }: any): Observable<JobCategory[]> => {
        return this.jobCategoryControllerService
            .jobCategoryControllerFind({})
            .pipe(
                tap((response: JobCategoryControllerFind200Response) => {
                    if (!response.success || response.statusCode !== 200) {
                        alert('Error: La solicitud jobCategory no fue exitosa');
                        throw new Error('Error en la respuesta del servidor');
                    }
                }),
                catchError((error) => {
                    console.error('Error fetching job categories:', error);
                    return [];
                })
            );
    };

    postPhotos = (img: any) => {
        return this.fileUploadControllerService.fileUploadControllerFileUpload(
            img
        );
    };

    createUsersByExcel = (excel: any) => {
        return this.userControllerService.userControllerUploadUsersByExcel(
            excel
        );
    };

    previewUsersByExcel = (excel: any) => {
        return this.userControllerService.userControllerPreviewUsersByExcel(
            excel
        );
    };

    createUser = (userData: User | any) => {
        const {
            password,
            departmentId,
            jobPositionId,
            jobCategoryId,
            ...user
        } = userData;

        return this.userControllerService
            .userControllerCreate({
                defaultTenant: 1,
                ...user,
                ...(jobPositionId && { jobPositionId }),
                ...(jobCategoryId && { jobCategoryId }),
                ...(departmentId && { departmentId }),
            })
            .pipe();
    };

    updateUser = (userData: User) => {
        const {
            id = 0,
            jobPositionId,
            jobCategoryId,
            departmentId,
            businessUnitId,
            ...user
        } = userData;
        return this.userControllerService.userControllerUpdateById(id, {
            defaultTenant: 1,
            ...user,
            ...(jobPositionId && { jobPositionId }),
            ...(jobCategoryId && { jobCategoryId }),
            ...(departmentId && { departmentId }),
            ...(businessUnitId && { businessUnitId }),
        });
    };

    deleteUser = (userId: number) =>
        this.userControllerService.userControllerDeleteById(userId);

    getPublicationByUser = (
        userId: number
    ): Observable<UserControllerJsonUserPublications200Response[]> => {
        return this.userControllerService
            .userControllerJsonUserPublications(userId)
            .pipe(
                tap(
                    (
                        response: UserControllerJsonUserPublications200Response
                    ) => {
                        if (!response.success || response.statusCode !== 200) {
                        }
                    }
                ),
                catchError((error) => {
                    console.error('Error fetching user publications:', error);
                    return [];
                })
            );
    };

    getCurrentUser = () => {
        const filters: any | UsersFilter = JSON.stringify({
            where: {},
            include: [
                {
                    relation: 'company',
                },
                {
                    relation: 'jobPosition',
                },
                {
                    relation: 'department',
                },
                {
                    relation: 'jobCategory',
                },
            ],
        });

        return this.userControllerService.userControllerGetCurrentUserToShow(
            filters
        );
    };

    // Métodos a añadir en user.service.ts

    jsonBirthday(filters: UsersFilter): Observable<any> {
        return this.userControllerService
            .userControllerJsonBirthday(filters)
            .pipe(
                tap((response: any) => {
                    if (!response.success) {
                        console.warn(
                            'Error en la respuesta de cumpleaños:',
                            response.message
                        );
                    }
                }),
                catchError((error) => {
                    console.error('Error fetching birthdays:', error);
                    return of({ success: false, data: [] });
                })
            );
    }

    jsonNewEmployees(filters: UsersFilter): Observable<any> {
        return this.userControllerService
            .userControllerJsonNewEmployees(filters)
            .pipe(
                tap((response: any) => {
                    if (!response.success) {
                        console.warn(
                            'Error en la respuesta de nuevos empleados:',
                            response.message
                        );
                    }
                }),
                catchError((error) => {
                    console.error('Error fetching new employees:', error);
                    return of({ success: false, data: [] });
                })
            );
    }

    checkUserRole = (email: any) => {
        return this.humanCapitalByCompanyControllerService.humanCapitalByCompanyControllerCheck(
            email
        );
    };

    updatePassword = (passwordData: any) => {
        return this.userControllerService.userControllerUpdatePassword(
            passwordData
        );
    };
    forgottenPassword = (passwordData: any) => {
        return this.userControllerService.userControllerForgottenPassword(
            passwordData
        );
    };
    sendRestorePassEmail = (passwordData: any) => {
        return this.userControllerService.userControllerSendRestorePassEmail(
            passwordData
        );
    };
    desactivateUser = (userId: number, desactivateData: User) => {
        const { otherInactiveReason, ...user } = desactivateData;
        return this.userControllerService.userControllerInactiveUserById(
            userId,
            {
                ...user,
                ...(otherInactiveReason && { otherInactiveReason }),
            }
        );
    };
    getUserById(id): Observable<any> {
        const filters: UsersFilter | any = JSON.stringify({
            fields: {
                id: true,
                firstName: true,
                middleName: true,
                lastName: true,
                email: true,
                photoProfileUrl: true,
            },
        });

        return this.userControllerService
            .userControllerFindById(id, filters)
            .pipe(
                map((user: any) => {
                    if (user) {
                        return {
                            ...user,
                            fullName: this.getFullName(user),
                        };
                    }
                    return null;
                }),
                catchError((error) => {
                    console.error('Error fetching user by ID:', error);
                    return of(null);
                })
            );
    }
    private getFullName(person: any): string {
        if (!person) return '';
        const firstName = person.firstName || '';
        const middleName =
            person.middleName && person.middleName !== ' '
                ? person.middleName
                : '';
        const lastName = person.lastName || '';

        return [firstName, middleName, lastName].filter(Boolean).join(' ');
    }
    // En tu user.service.ts, agregar:
    getPublicationsFeed(userId: number): Observable<any[]> {
        return this.userControllerService
            .userControllerGetAllPublicationsPerUser(userId)
            .pipe(
                tap((response: any) => {
                    if (!response.success || response.statusCode !== 200) {
                        console.warn('Error en la respuesta del feed:', response.message);
                    }
                }),
                map((response: any) => response.success ? (response.data || []) : []),
                catchError((error) => {
                    console.error('Error fetching publications feed:', error);
                    return of([]);
                })
            );
    }
    /** Página del feed; a diferencia de getPublicationsFeed, propaga el error para poder reintentar. */
    getPublicationsFeedPage(userId: number, limit: number, offset: number): Observable<any[]> {
        return this.userControllerService
            .userControllerGetAllPublicationsPerUser(userId, limit, offset)
            .pipe(map((response: any) => (response.success ? response.data || [] : [])));
    }
    getCities(): Observable<any[]> {
        return this.cityControllerService.cityControllerFind()
            .pipe(
                tap((response: any) => {

                }),
                map((response: any) => response.success ? (response.data || []) : []),
                catchError((error) => {
                    console.error('Error fetching cities:', error);
                    return of([]);
                })
            );
    }
}
interface UserToTable {
    users: User[];
    total: number;
}
